import React, { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc, collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { initializeApp } from 'firebase/app';

const firebaseConfig = {
  apiKey: "AIzaSyDJ_lusSO40qPa2pKdvwOOexRPU2p4Vsro",
  authDomain: "tradeahv2.firebaseapp.com",
  projectId: "tradeahv2",
  storageBucket: "tradeahv2.firebasestorage.app",
  messagingSenderId: "398274404066",
  appId: "1:398274404066:web:74be764b202f62b91f08bb"
};

initializeApp(firebaseConfig);
const auth = getAuth();
const db = getFirestore();

export default function ClientDashboard() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [activeSignals, setActiveSignals] = useState([]);
  const [closedSignals, setClosedSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [symbolsMap, setSymbolsMap] = useState({});
  const [filterType, setFilterType] = useState('all');
  const [adsData, setAdsData] = useState([]);
  const [darkMode, setDarkMode] = useState(false);
  
  // مؤشرات الأداء
  const [kpis, setKPIs] = useState({
    totalActive: 0,
    totalClosed: 0,
    overallSuccessRate: 0,
    overallProfitRate: 0,
    intradayActive: 0,
    shortActive: 0,
    mediumActive: 0
  });

  // تحميل تفضيل الوضع من localStorage
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    setDarkMode(savedDarkMode);
  }, []);

  // حفظ تفضيل الوضع
  useEffect(() => {
    localStorage.setItem('darkMode', darkMode.toString());
  }, [darkMode]);

  // تحميل رموز TradingView
  useEffect(() => {
    const loadSymbols = async () => {
      try {
        const response = await fetch('/symbols.csv');
        const text = await response.text();
        const lines = text.split('\n').slice(1);
        const map = {};
        lines.forEach(line => {
          if (line.trim()) {
            const parts = line.split(',');
            if (parts.length >= 4) {
              const [symbol, market, name_ar, tradingview_symbol] = parts;
              map[symbol] = tradingview_symbol.trim();
            }
          }
        });
        setSymbolsMap(map);
      } catch (error) {
        console.error('Error loading symbols:', error);
      }
    };
    loadSymbols();
  }, []);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
      } else {
        window.location.href = '/signup';
      }
    });

    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const fetchUserData = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.trial_status !== 'active') {
            alert('حسابك غير مفعل بعد. يرجى الانتظار حتى يتم تفعيله من قبل الدعم.');
            window.location.href = '/signup';
            return;
          }
          setUserProfile(userData);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching user ", error);
        setLoading(false);
      }
    };

    fetchUserData();

    // جلب الإعلانات
    const adsQ = query(collection(db, 'ads'), where('is_active', '==', true));
    const adsUnsubscribe = onSnapshot(adsQ, (snapshot) => {
      const adsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAdsData(adsList);
    });

    // جلب التوصيات
    const activeQ = query(collection(db, 'signals_v2'), where('status', '==', 'active'), orderBy('created_at', 'desc'));
    const activeUnsubscribe = onSnapshot(activeQ, (snapshot) => {
      const activeList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setActiveSignals(activeList);
    });

    const closedQ = query(collection(db, 'signals_v2'), where('status', '!=', 'active'), orderBy('created_at', 'desc'));
    const closedUnsubscribe = onSnapshot(closedQ, (snapshot) => {
      const closedList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setClosedSignals(closedList);
    });

    return () => {
      adsUnsubscribe();
      activeUnsubscribe();
      closedUnsubscribe();
    };
  }, [user]);

  // حساب مؤشرات الأداء
  useEffect(() => {
    if (activeSignals.length === 0 && closedSignals.length === 0) return;

    const intradaySignals = activeSignals.filter(s => s.signal_type === 'intraday');
    const shortSignals = activeSignals.filter(s => s.signal_type === 'short');
    const mediumSignals = activeSignals.filter(s => s.signal_type === 'medium');
    
    const calculateMetrics = (closed) => {
      if (closed.length === 0) return { successRate: 0, profitRate: 0 };
      
      const successful = closed.filter(s => s.status === 'achieved').length;
      const successRate = Math.round((successful / closed.length) * 100);
      
      let totalProfit = 0;
      let totalLoss = 0;
      let profitableTrades = 0;
      let lossTrades = 0;
      
      closed.forEach(signal => {
        if (signal.status === 'achieved') {
          const profit = ((signal.take_profit - signal.entry_price) / signal.entry_price) * 100;
          totalProfit += profit;
          profitableTrades++;
        } else {
          const loss = ((signal.entry_price - signal.stop_loss) / signal.entry_price) * 100;
          totalLoss += loss;
          lossTrades++;
        }
      });
      
      const avgProfit = profitableTrades > 0 ? totalProfit / profitableTrades : 0;
      const avgLoss = lossTrades > 0 ? totalLoss / lossTrades : 0;
      const profitRate = Math.round(((avgProfit * profitableTrades) - (avgLoss * lossTrades)) / closed.length);
      
      return { successRate, profitRate };
    };
    
    const overallMetrics = calculateMetrics(closedSignals);
    
    setKPIs({
      totalActive: activeSignals.length,
      totalClosed: closedSignals.length,
      overallSuccessRate: overallMetrics.successRate,
      overallProfitRate: overallMetrics.profitRate,
      intradayActive: intradaySignals.length,
      shortActive: shortSignals.length,
      mediumActive: mediumSignals.length
    });
    
  }, [activeSignals, closedSignals]);

  const filteredSignals = () => {
    if (filterType === 'all') return activeSignals;
    if (filterType === 'intraday') return activeSignals.filter(s => s.signal_type === 'intraday');
    if (filterType === 'short') return activeSignals.filter(s => s.signal_type === 'short');
    if (filterType === 'medium') return activeSignals.filter(s => s.signal_type === 'medium');
    return activeSignals;
  };

  if (loading) {
    return (
      <div style={{ 
        padding: '20px', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        minHeight: '50vh',
        backgroundColor: darkMode ? '#0f172a' : '#f8fafc'
      }}>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center'
        }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #1e40af', 
            borderTopColor: 'transparent', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite' 
          }}></div>
          <div style={{ marginTop: '16px', color: '#1e40af', fontWeight: '500' }}>
            جاري تحميل البيانات...
          </div>
        </div>
      </div>
    );
  }

  const endDate = userProfile?.subscription?.end_date 
    ? new Date(userProfile.subscription.end_date).toLocaleDateString('ar-EG') 
    : 'غير متوفر';

  // أنماط الوضع الليلي
  const getStyles = () => ({
    container: {
      padding: '15px',
      fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
      maxWidth: '1200px',
      margin: '0 auto',
      backgroundColor: darkMode ? '#0f172a' : '#f8fafc',
      color: darkMode ? '#f8fafc' : '#0f172a'
    },
    welcomeCard: {
      background: darkMode ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)' : 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
      padding: '20px',
      borderRadius: '16px',
      color: 'white',
      marginBottom: '20px',
      boxShadow: darkMode ? '0 6px 16px rgba(0, 0, 0, 0.5)' : '0 6px 16px rgba(30, 64, 175, 0.3)',
      textAlign: 'center'
    },
    kpiCard: {
      backgroundColor: darkMode ? '#1e293b' : '#f8fafc',
      padding: '20px',
      borderRadius: '16px',
      border: darkMode ? '1px solid #334155' : '1px solid #e2e8f0',
      marginBottom: '20px',
      color: darkMode ? '#f8fafc' : '#0f172a'
    },
    kpiItem: {
      textAlign: 'center',
      padding: '12px',
      backgroundColor: darkMode ? '#334155' : 'white',
      borderRadius: '10px',
      color: darkMode ? '#f8fafc' : '#0f172a'
    },
    adCard: {
      backgroundColor: darkMode ? '#1e293b' : 'white',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: darkMode ? '0 3px 6px rgba(0,0,0,0.3)' : '0 3px 6px rgba(0,0,0,0.1)',
      border: darkMode ? '1px solid #334155' : '1px solid #f1f5f9',
      color: darkMode ? '#f8fafc' : '#0f172a'
    },
    signalCard: {
      backgroundColor: darkMode ? '#1e293b' : 'white',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: darkMode ? '0 2px 4px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
      border: darkMode ? '1px solid #334155' : '1px solid #f1f5f9',
      color: darkMode ? '#f8fafc' : '#0f172a'
    },
    detailItem: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '8px',
      backgroundColor: darkMode ? '#334155' : 'white',
      borderRadius: '8px',
      border: darkMode ? '1px solid #475569' : '1px solid #e2e8f0',
      color: darkMode ? '#f8fafc' : '#0f172a'
    },
    button: {
      backgroundColor: '#1e40af',
      color: 'white',
      border: 'none',
      padding: '12px 24px',
      borderRadius: '12px',
      fontSize: '14px',
      fontWeight: '700',
      cursor: 'pointer',
      boxShadow: darkMode ? '0 3px 6px rgba(30, 64, 175, 0.4)' : '0 3px 6px rgba(30, 64, 175, 0.2)',
      transition: 'transform 0.2s, background-color 0.2s'
    },
    toggleButton: {
      backgroundColor: darkMode ? '#334155' : '#e2e8f0',
      color: darkMode ? '#f8fafc' : '#1e293b',
      border: 'none',
      width: '36px',
      height: '36px',
      borderRadius: '18px',
      cursor: 'pointer',
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }
  });

  const styles = getStyles();

  return (
    <div style={styles.container}>
      {/* شريط الترحيب */}
      <div style={styles.welcomeCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700' }}>
            أهلاً بك، {userProfile?.first_name || 'العميل الكريم'}!
          </h2>
          <button
            onClick={() => setDarkMode(!darkMode)}
            style={styles.toggleButton}
          >
            {darkMode ? '☀️' : '🌙'}
          </button>
        </div>
        <p style={{ margin: '8px 0 0 0', fontSize: '14px', opacity: '0.9' }}>
          انتهت تجربتك في: <strong>{endDate}</strong>
        </p>
      </div>

      {/* مؤشرات الأداء */}
      <div style={styles.kpiCard}>
        <h3 style={{ color: '#1e40af', marginBottom: '16px', fontSize: '20px', fontWeight: '700', textAlign: 'center' }}>
          مؤشرات الأداء الخاصة بك
        </h3>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
          gap: '12px'
        }}>
          <div style={styles.kpiItem}>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e40af' }}>
              {kpis.totalActive}
            </div>
            <div style={{ fontSize: '12px', color: darkMode ? '#94a3b8' : '#64748b' }}>
              توصيات نشطة
            </div>
          </div>
          <div style={styles.kpiItem}>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e40af' }}>
              {kpis.totalClosed}
            </div>
            <div style={{ fontSize: '12px', color: darkMode ? '#94a3b8' : '#64748b' }}>
              توصيات مغلقة
            </div>
          </div>
          <div style={styles.kpiItem}>
            <div style={{ fontSize: '20px', fontWeight: '700', color: kpis.overallSuccessRate >= 70 ? '#16a34a' : '#dc2626' }}>
              {kpis.overallSuccessRate}%
            </div>
            <div style={{ fontSize: '12px', color: darkMode ? '#94a3b8' : '#64748b' }}>
              معدل النجاح
            </div>
          </div>
          <div style={styles.kpiItem}>
            <div style={{ fontSize: '20px', fontWeight: '700', color: kpis.overallProfitRate >= 0 ? '#16a34a' : '#dc2626' }}>
              {kpis.overallProfitRate >= 0 ? '+' : ''}{kpis.overallProfitRate}%
            </div>
            <div style={{ fontSize: '12px', color: darkMode ? '#94a3b8' : '#64748b' }}>
              معدل الربح
            </div>
          </div>
        </div>
        
        {(kpis.intradayActive > 0 || kpis.shortActive > 0 || kpis.mediumActive > 0) && (
          <div style={{ 
            padding: '16px', 
            backgroundColor: darkMode ? '#334155' : 'white', 
            borderRadius: '12px', 
            border: darkMode ? '1px solid #475569' : '1px solid #e2e8f0',
            marginTop: '16px'
          }}>
            <h4 style={{ color: '#1e40af', marginBottom: '12px', fontSize: '16px', fontWeight: '600', textAlign: 'center' }}>
              📈 توزيع التوصيات النشطة
            </h4>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', 
              gap: '12px'
            }}>
              {kpis.intradayActive > 0 && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#c2410c' }}>
                    {kpis.intradayActive}
                  </div>
                  <div style={{ fontSize: '12px', color: darkMode ? '#fcd34d' : '#9a3412' }}>
                    لحظية
                  </div>
                </div>
              )}
              {kpis.shortActive > 0 && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#1e40af' }}>
                    {kpis.shortActive}
                  </div>
                  <div style={{ fontSize: '12px', color: darkMode ? '#93c5fd' : '#1e40af' }}>
                    قصيرة
                  </div>
                </div>
              )}
              {kpis.mediumActive > 0 && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#065f46' }}>
                    {kpis.mediumActive}
                  </div>
                  <div style={{ fontSize: '12px', color: darkMode ? '#6ee7b7' : '#065f46' }}>
                    متوسطة
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* الإعلان */}
      {user && (
        <div style={{ marginBottom: '20px', direction: 'rtl' }}>
          {(() => {
            const now = new Date();
            const activeAds = adsData
              .filter(ad => ad.is_active && new Date(ad.start_date) <= now && new Date(ad.end_date) >= now)
              .sort((a, b) => (b.priority || 0) - (a.priority || 0));
            const currentAd = activeAds[0];
            if (!currentAd) return null;
            
            return (
              <div style={styles.adCard}>
                <div style={{ 
                  padding: '14px', 
                  borderBottom: darkMode ? '1px solid #475569' : '1px solid #f1f5f9',
                  backgroundColor: darkMode ? '#334155' : '#f8fafc',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '8px'
                }}>
                  <h3 style={{ 
                    margin: 0, 
                    color: '#1e40af', 
                    fontSize: '16px', 
                    fontWeight: '700',
                    textAlign: 'right',
                    flex: 1
                  }}>
                    📢 {currentAd.title || 'عروض خاصة لعملائنا'}
                  </h3>
                </div>
                
                <div style={{ 
                  padding: '16px',
                  display: 'flex',
                  gap: '16px',
                  flexDirection: 'row-reverse',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ flex: '1 1 200px', textAlign: 'right' }}>
                    <h4 style={{ margin: '0 0 10px 0', color: darkMode ? '#f8fafc' : '#0f172a', fontSize: '14px', fontWeight: '700' }}>
                      {currentAd.title}
                    </h4>
                    <p style={{ margin: '0 0 12px 0', color: darkMode ? '#94a3b8' : '#64748b', fontSize: '13px', lineHeight: '1.5' }}>
                      {currentAd.description}
                    </p>
                    <a
                      href={currentAd.target_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        backgroundColor: '#1e40af',
                        color: 'white',
                        textDecoration: 'none',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        float: 'left'
                      }}
                    >
                      ← تفاصيل أكثر
                    </a>
                  </div>
                  <div style={{ flex: '0 0 100px' }}>
                    {currentAd.image_url ? (
                      <img
                        src={currentAd.image_url}
                        alt={currentAd.title}
                        style={{
                          width: '100%',
                          height: '100px',
                          objectFit: 'cover',
                          borderRadius: '6px'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100px',
                        backgroundColor: '#1e40af',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '32px'
                      }}>
                        📢
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* أزرار التصفية */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        gap: '8px',
        marginBottom: '20px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => setFilterType('all')}
          style={{
            padding: '8px 16px',
            backgroundColor: filterType === 'all' ? '#1e40af' : (darkMode ? '#334155' : '#e2e8f0'),
            color: filterType === 'all' ? 'white' : (darkMode ? '#f8fafc' : '#1e293b'),
            border: 'none',
            borderRadius: '16px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 'bold',
            whiteSpace: 'nowrap'
          }}
        >
          📊 جميع ({activeSignals.length})
        </button>
        {kpis.intradayActive > 0 && (
          <button
            onClick={() => setFilterType('intraday')}
            style={{
              padding: '8px 16px',
              backgroundColor: filterType === 'intraday' ? '#c2410c' : (darkMode ? '#334155' : '#fef3c7'),
              color: filterType === 'intraday' ? 'white' : (darkMode ? '#fcd34d' : '#9a3412'),
              border: 'none',
              borderRadius: '16px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 'bold',
              whiteSpace: 'nowrap'
            }}
          >
            🚀 اللحظية ({kpis.intradayActive})
          </button>
        )}
        {kpis.shortActive > 0 && (
          <button
            onClick={() => setFilterType('short')}
            style={{
              padding: '8px 16px',
              backgroundColor: filterType === 'short' ? '#1e40af' : (darkMode ? '#334155' : '#dbeafe'),
              color: filterType === 'short' ? 'white' : (darkMode ? '#93c5fd' : '#1d4ed8'),
              border: 'none',
              borderRadius: '16px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 'bold',
              whiteSpace: 'nowrap'
            }}
          >
            ⏳ القصيرة ({kpis.shortActive})
          </button>
        )}
        {kpis.mediumActive > 0 && (
          <button
            onClick={() => setFilterType('medium')}
            style={{
              padding: '8px 16px',
              backgroundColor: filterType === 'medium' ? '#065f46' : (darkMode ? '#334155' : '#dcfce7'),
              color: filterType === 'medium' ? 'white' : (darkMode ? '#6ee7b7' : '#166534'),
              border: 'none',
              borderRadius: '16px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 'bold',
              whiteSpace: 'nowrap'
            }}
          >
            📈 المتوسطة ({kpis.mediumActive})
          </button>
        )}
      </div>

  {/* كروت التوصيات - تصميم متجاوب ذكي */}
<div style={{ 
  display: 'grid', 
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', // ← ← ← السر هنا
  gap: '20px',
  marginBottom: '20px'
}}>
  {filteredSignals().length === 0 ? (
    <div style={{ 
      backgroundColor: darkMode ? '#1e293b' : 'white', 
      padding: '30px',
      borderRadius: '16px', 
      textAlign: 'center',
      border: darkMode ? '2px dashed #334155' : '2px dashed #e2e8f0',
      boxShadow: darkMode ? '0 3px 6px rgba(0,0,0,0.3)' : '0 3px 6px rgba(0,0,0,0.08)',
      gridColumn: '1 / -1'
    }}>
      <div style={{ fontSize: '40px', marginBottom: '12px', color: darkMode ? '#475569' : '#cbd5e1' }}>📉</div>
      <h4 style={{ color: darkMode ? '#94a3b8' : '#64748b', margin: '0 0 10px 0', fontSize: '16px', fontWeight: '600' }}>
        لا توجد توصيات {filterType === 'all' ? '' : 
                      filterType === 'intraday' ? 'لحظية' : 
                      filterType === 'short' ? 'قصيرة' : 'متوسطة'} نشطة
      </h4>
      <p style={{ color: darkMode ? '#64748b' : '#94a3b8', margin: 0, fontSize: '14px' }}>
        تحقق لاحقًا للحصول على فرص جديدة
      </p>
    </div>
  ) : (
    filteredSignals().map(signal => {
      const isNew = signal.created_at && (Date.now() - new Date(signal.created_at).getTime()) < (24 * 60 * 60 * 1000);
      
      return (
        <div 
          key={signal.id}
          style={{ 
            backgroundColor: isNew ? (darkMode ? '#334155' : '#dbeafe') : (darkMode ? '#1e293b' : 'white'),
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: darkMode ? '0 2px 4px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
            border: darkMode ? '1px solid #334155' : '1px solid #f1f5f9',
            transition: 'transform 0.2s',
            cursor: 'pointer',
            minWidth: '300px', // ← ← ← الحد الأدنى للعرض
            width: '100%'     // ← ← ← يتكيف مع الحاوية
          }}
          onClick={() => window.location.href = `/signal/${signal.id}`}
        >
          {/* رأس الكارت */}
          <div style={{ 
            padding: '12px 16px',
            borderBottom: darkMode ? '1px solid #334155' : '1px solid #e2e8f0',
            backgroundColor: isNew ? (darkMode ? '#475569' : '#eff6ff') : (darkMode ? '#1e293b' : '#f8fafc'),
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ fontSize: '16px' }}>
                  {signal.action === 'BUY' ? '🟢' : '🔴'}
                </div>
                <h4 style={{ 
                  margin: 0, 
                  fontSize: '16px', 
                  fontWeight: '700',
                  color: darkMode ? '#f8fafc' : '#0f172a',
                  maxWidth: '120px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {signal.ticker}
                </h4>
              </div>
              {signal.ticker_name && (
                <div style={{ 
                  fontSize: '12px', 
                  color: darkMode ? '#94a3b8' : '#64748b',
                  marginTop: '2px',
                  maxWidth: '120px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  ({signal.ticker_name})
                </div>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ 
                backgroundColor: signal.signal_type === 'intraday' ? (darkMode ? '#475569' : '#fff7ed') : 
                               signal.signal_type === 'short' ? (darkMode ? '#475569' : '#f0f9ff') : (darkMode ? '#475569' : '#f0fdf4'),
                color: signal.signal_type === 'intraday' ? (darkMode ? '#fcd34d' : '#c2410c') : 
                        signal.signal_type === 'short' ? (darkMode ? '#93c5fd' : '#1e40af') : (darkMode ? '#6ee7b7' : '#065f46'),
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '11px',
                fontWeight: '600',
                marginBottom: '4px',
                whiteSpace: 'nowrap'
              }}>
                {signal.signal_type === 'intraday' ? 'لحظية' : 
                 signal.signal_type === 'short' ? 'قصيرة' : 'متوسطة'}
              </div>
              {signal.created_at && (
                <div style={{ 
                  fontSize: '10px', 
                  color: darkMode ? '#64748b' : '#64748b',
                  fontWeight: '500'
                }}>
                  {new Date(signal.created_at).toLocaleDateString('ar-EG', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              )}
            </div>
          </div>
          
          {/* التشارت */}
          <div style={{ height: '280px', overflow: 'hidden' }}>
            <iframe
              src={`https://s.tradingview.com/widgetembed/?symbol=${symbolsMap[signal.ticker] || signal.ticker}&interval=60&hidesidetoolbar=1&symboledit=0&saveimage=0&toolbarbg=F1F3F6&studies=[]&theme=light&withdateranges=1&hideideas=1`}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
              title={`Chart for ${signal.ticker}`}
              loading="lazy"
            />
          </div>
          
{/* تفاصيل التوصية - سطر واحد مع أيقونات */}
<div style={{ 
  padding: '12px 16px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '15px',
  backgroundColor: isNew ? (darkMode ? '#475569' : '#eff6ff') : (darkMode ? '#1e293b' : '#f8fafc'),
  fontSize: '12px',
  borderBottomLeftRadius: '12px',
  borderBottomRightRadius: '12px'
}}>
  {/* سعر الدخول */}
  <div style={{ 
    display: 'flex', 
    alignItems: 'center', 
    gap: '6px',
    padding: '6px 10px',
    backgroundColor: darkMode ? '#334155' : 'white',
    borderRadius: '8px',
    border: darkMode ? '1px solid #475569' : '1px solid #e2e8f0',
    minWidth: '100px',
    justifyContent: 'center'
  }}>
    <span style={{ fontSize: '14px', color: '#1e40af' }}>📌</span>
    <div style={{ textAlign: 'left' }}>
      <div style={{ fontSize: '10px', color: darkMode ? '#94a3b8' : '#64748b', fontWeight: '600' }}>الدخول</div>
      <div style={{ fontWeight: '800', color: '#1e40af', fontSize: '13px' }}>{signal.entry_price}</div>
    </div>
  </div>
  
  {/* الهدف */}
  <div style={{ 
    display: 'flex', 
    alignItems: 'center', 
    gap: '6px',
    padding: '6px 10px',
    backgroundColor: darkMode ? '#334155' : 'white',
    borderRadius: '8px',
    border: darkMode ? '1px solid #475569' : '1px solid #e2e8f0',
    minWidth: '100px',
    justifyContent: 'center'
  }}>
    <span style={{ fontSize: '14px', color: '#16a34a' }}>🎯</span>
    <div style={{ textAlign: 'left' }}>
      <div style={{ fontSize: '10px', color: darkMode ? '#94a3b8' : '#64748b', fontWeight: '600' }}>الهدف</div>
      <div style={{ fontWeight: '800', color: '#16a34a', fontSize: '13px' }}>{signal.take_profit}</div>
    </div>
  </div>
  
  {/* وقف الخسارة */}
  <div style={{ 
    display: 'flex', 
    alignItems: 'center', 
    gap: '6px',
    padding: '6px 10px',
    backgroundColor: darkMode ? '#334155' : 'white',
    borderRadius: '8px',
    border: darkMode ? '1px solid #475569' : '1px solid #e2e8f0',
    minWidth: '100px',
    justifyContent: 'center'
  }}>
    <span style={{ fontSize: '14px', color: '#dc2626' }}>🛡️</span>
    <div style={{ textAlign: 'left' }}>
      <div style={{ fontSize: '10px', color: darkMode ? '#94a3b8' : '#64748b', fontWeight: '600' }}>وقف</div>
      <div style={{ fontWeight: '800', color: '#dc2626', fontSize: '13px' }}>{signal.stop_loss}</div>
    </div>
  </div>
  
  {/* درجة الثقة */}
  <div style={{ 
    display: 'flex', 
    alignItems: 'center', 
    gap: '6px',
    padding: '6px 10px',
    backgroundColor: darkMode ? '#334155' : 'white',
    borderRadius: '8px',
    border: darkMode ? '1px solid #475569' : '1px solid #e2e8f0',
    minWidth: '100px',
    justifyContent: 'center'
  }}>
    <span style={{ fontSize: '14px', color: '#1e40af' }}>💯</span>
    <div style={{ textAlign: 'left' }}>
      <div style={{ fontSize: '10px', color: darkMode ? '#94a3b8' : '#64748b', fontWeight: '600' }}>الثقة</div>
      <div style={{ fontWeight: '800', color: '#1e40af', fontSize: '13px' }}>{signal.confidence_score}%</div>
    </div>
  </div>
</div>
          
          {/* قدم التفاصيل */}
          <div style={{ 
            padding: '8px 16px',
            borderTop: darkMode ? '1px solid #334155' : '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '11px'
          }}>
            <div style={{ 
              backgroundColor: signal.expert_id ? (darkMode ? '#475569' : '#dbeafe') : (darkMode ? '#475569' : '#f0fdf4'),
              color: signal.expert_id ? (darkMode ? '#93c5fd' : '#1d4ed8') : (darkMode ? '#6ee7b7' : '#065f46'),
              padding: '2px 6px',
              borderRadius: '8px',
              fontWeight: '600',
              fontSize: '10px'
            }}>
              {signal.expert_id ? `TradeAH_${signal.expert_id.split('_').pop()}` : 'النظام'}
            </div>
            <div style={{ 
              color: '#1e40af', 
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '11px'
            }}>
              التفاصيل →
            </div>
          </div>
        </div>
      );
    })
  )}
</div>

      {/* مؤشرات إضافية */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
        gap: '16px',
        marginBottom: '20px'
      }}>
        <div style={{ 
          backgroundColor: darkMode ? '#1e293b' : 'white',
          borderRadius: '16px',
          padding: '16px',
          textAlign: 'center',
          boxShadow: darkMode ? '0 4px 8px rgba(0,0,0,0.3)' : '0 4px 8px rgba(0,0,0,0.08)',
          border: darkMode ? '1px solid #334155' : '1px solid #f1f5f9',
          color: darkMode ? '#f8fafc' : '#0f172a'
        }}>
          <div style={{ fontSize: '12px', color: darkMode ? '#94a3b8' : '#64748b', marginBottom: '6px', fontWeight: '600' }}>
            الأيام المتبقية
          </div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#1e40af' }}>
            {userProfile?.subscription?.end_date ? 
              Math.max(0, Math.ceil((new Date(userProfile.subscription.end_date) - new Date()) / (1000 * 60 * 60 * 24))) 
              : '14'}
          </div>
        </div>
        
        <div style={{ 
          backgroundColor: darkMode ? '#1e293b' : 'white',
          borderRadius: '16px',
          padding: '16px',
          textAlign: 'center',
          boxShadow: darkMode ? '0 4px 8px rgba(0,0,0,0.3)' : '0 4px 8px rgba(0,0,0,0.08)',
          border: darkMode ? '1px solid #334155' : '1px solid #f1f5f9',
          color: darkMode ? '#f8fafc' : '#0f172a'
        }}>
          <div style={{ fontSize: '12px', color: darkMode ? '#94a3b8' : '#64748b', marginBottom: '6px', fontWeight: '600' }}>
            الأسواق
          </div>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#1e40af', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {userProfile?.subscribed_markets?.map(m => m.substring(0, 3)).join(', ') || 'EGX'}
          </div>
        </div>
      </div>

      {/* زر الأرشيف */}
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <a 
          href="/archive" 
          style={styles.button}
        >
          📊 الأرشيف ({closedSignals.length})
        </a>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @media (max-width: 768px) {
            .dashboard-container {
              padding: 10px;
            }
            
            .card {
              border-radius: 10px;
              padding: 12px;
            }
            
            .filter-buttons {
              gap: 6px;
            }
            
            .filter-button {
              padding: 6px 12px;
              font-size: 12px;
            }
          }
        `}
      </style>
    </div>
  );
}